import {
  usePlaceStateA,
  usePlaceStateB,
  useSelectedRouteIndexState,
  useJourneyResultState,
} from '../../stores/journey-planner/JourneySearchState'
import { useGoogleLoadState, getMap } from '../../stores/GoogleMapState'
import { useEffect } from 'react'
import { JourneyResult, JourneySearchPoint, MAP_Z_INDEX, RelatedItem } from '../../types/JourneyPlannerTypes'
import { useFilterState } from '../../stores/MapFiltersState'
import { clearMarkerCollection, useMarkerCollection } from './MarkerCollection'
import { useLocation, useHistory } from 'react-router-dom'
import { getClosures } from '../../stores/national-data/delays'
import {
  createStartMarker,
  createEndMarker,
  removeWaypointMarker,
  RouteWaypointMarker,
} from '../../util/route-pins'
import { getLiveRoutes, RoutePolylineOptions } from '../../util/google-route-loader'

/*
 * createPolylines() returns an array because a single route can expand into
 * multiple polyline segments (e.g. one per speed-reading interval for
 * traffic-aware rendering). Today's field mask doesn't request that, so each
 * call returns a length-1 array — but we store them as arrays to handle both
 * shapes uniformly.
 */
let selectedPolylineFills: google.maps.Polyline[] = []
let selectedPolylineBorders: google.maps.Polyline[] = []
let alternatePolylineFills: google.maps.Polyline[] = []
let alternatePolylineBorders: google.maps.Polyline[] = []
let startMarker: RouteWaypointMarker | null = null
let endMarker: RouteWaypointMarker | null = null

type LiveRoute = {
  createPolylines?: (options?: RoutePolylineOptions) => google.maps.Polyline[]
}

let lastDirectionsHash: string

let pointA: google.maps.Data.Feature | null
let pointB: google.maps.Data.Feature | null

// Each route renders as two stacked polylines: a thicker border underneath
// and a coloured fill on top, to create a bordered line effect.
const ROUTE_STYLES = {
  selected: {
    borderColor: '#6b6b6b',
    borderWeight: 8,
    fillColor: '#709def',
    fillWeight: 6,
    fillOpacity: 1,
    clickable: false,
  },
  alternate: {
    borderColor: '#6b6b6b',
    borderWeight: 6,
    fillColor: '#b6b6b6',
    fillWeight: 4,
    fillOpacity: 0.8,
    clickable: true,
  },
} as const

interface BorderedPolylineOptions {
  borderColor: string
  borderWeight: number
  borderZIndex: number
  fillColor: string
  fillWeight: number
  fillOpacity: number
  fillZIndex: number
  clickable: boolean
  onClick?: () => void
}

const createBorderedPolyline = (
  map: google.maps.Map,
  path: google.maps.LatLngLiteral[],
  liveRoute: LiveRoute | undefined,
  opts: BorderedPolylineOptions,
): { borders: google.maps.Polyline[]; fills: google.maps.Polyline[] } => {
  const borderOptions: google.maps.PolylineOptions = {
    strokeColor: opts.borderColor,
    strokeWeight: opts.borderWeight,
    strokeOpacity: 1,
    clickable: false,
    zIndex: opts.borderZIndex,
  }
  const fillOptions: google.maps.PolylineOptions = {
    strokeColor: opts.fillColor,
    strokeWeight: opts.fillWeight,
    strokeOpacity: opts.fillOpacity,
    clickable: opts.clickable,
    zIndex: opts.fillZIndex,
  }

  // Preferred path: the live Route instance from computeRoutes carries a
  // createPolylines() helper that builds Polyline objects pre-shaped to the
  // Route's own path. We call it twice to produce the border-under-fill
  // stack we render today.
  if (liveRoute?.createPolylines) {
    const borders = liveRoute.createPolylines({ polylineOptions: borderOptions })
    const fills = liveRoute.createPolylines({ polylineOptions: fillOptions })
    borders.forEach((p) => p.setMap(map))
    fills.forEach((p) => p.setMap(map))
    if (opts.onClick) {
      fills.forEach((p) => p.addListener('click', opts.onClick!))
    }
    return { borders, fills }
  }

  // Fallback: legacy Directions API path (no live Route available).
  const border = new google.maps.Polyline({ ...borderOptions, path, map })
  const fill = new google.maps.Polyline({ ...fillOptions, path, map })
  if (opts.onClick) {
    fill.addListener('click', opts.onClick)
  }
  return { borders: [border], fills: [fill] }
}

const updatePoint = (
  map: google.maps.Map,
  feature: google.maps.Data.Feature | null,
  geometry: google.maps.LatLng | null,
) => {
  if (!map) return null
  if (feature) {
    map.data.remove(feature)
  }
  if (geometry) {
    return map.data.addGeoJson({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Point',
        coordinates: [geometry.lng(), geometry.lat()],
      },
    })[0]
  }
  return null
}

function clearRoutePolylines() {
  selectedPolylineFills.forEach((p) => p.setMap(null))
  selectedPolylineFills = []
  selectedPolylineBorders.forEach((p) => p.setMap(null))
  selectedPolylineBorders = []
  alternatePolylineFills.forEach((p) => p.setMap(null))
  alternatePolylineFills = []
  alternatePolylineBorders.forEach((p) => p.setMap(null))
  alternatePolylineBorders = []
}

function clearWaypointMarkers() {
  removeWaypointMarker(startMarker)
  startMarker = null
  removeWaypointMarker(endMarker)
  endMarker = null
}

const renderDirections = (
  map: google.maps.Map,
  journeyResult: JourneyResult | null,
  placeA: JourneySearchPoint | null,
  placeB: JourneySearchPoint | null,
  selectedRouteIndex: number,
  history: any,
  search: any,
) => {
  if (!map) return

  clearRoutePolylines()

  if (journeyResult) {
    const preserveViewport = lastDirectionsHash === journeyResult.locationHash
    const selectedRoute = journeyResult.routes[selectedRouteIndex]
    const liveRoutesForJourney = getLiveRoutes(journeyResult.locationHash)

    if (selectedRoute) {
      const path = selectedRoute.geometry.coordinates.map((c: number[]) => ({ lat: c[1], lng: c[0] }))
      const { borders, fills } = createBorderedPolyline(
        map,
        path,
        liveRoutesForJourney?.[selectedRouteIndex],
        {
          ...ROUTE_STYLES.selected,
          borderZIndex: MAP_Z_INDEX.directionsRendererBorder,
          fillZIndex: MAP_Z_INDEX.directionsRenderer,
        },
      )
      selectedPolylineBorders = borders
      selectedPolylineFills = fills
    }

    // Render alternate routes
    journeyResult.routes.forEach((route, i) => {
      if (i === selectedRouteIndex) return
      const altPath = route.geometry.coordinates.map((c: number[]) => ({ lat: c[1], lng: c[0] }))
      const { borders, fills } = createBorderedPolyline(map, altPath, liveRoutesForJourney?.[i], {
        ...ROUTE_STYLES.alternate,
        borderZIndex: MAP_Z_INDEX.alternateRoutesBorderBase + (i + 1) * 2,
        fillZIndex: MAP_Z_INDEX.alternateRoutesBase + (i + 1) * 2,
        onClick: () => {
          history.push(`/journey-planner/commute/${i}${search.toString()}`)
        },
      })
      alternatePolylineBorders.push(...borders)
      alternatePolylineFills.push(...fills)
    })

    // A/B waypoint markers
    clearWaypointMarkers()
    if (placeA && placeB) {
      const startPos = { lat: placeA.geometry.lat(), lng: placeA.geometry.lng() }
      const endPos = { lat: placeB.geometry.lat(), lng: placeB.geometry.lng() }
      startMarker = createStartMarker(startPos, map)
      endMarker = createEndMarker(endPos, map)
    }

    // Fit bounds on first load for this set of routes
    if (!preserveViewport && selectedRoute) {
      const bounds = new google.maps.LatLngBounds()
      selectedRoute.geometry.coordinates.forEach((c: number[]) => {
        bounds.extend({ lat: c[1], lng: c[0] })
      })
      map.fitBounds(bounds)
    }

    lastDirectionsHash = journeyResult.locationHash
    pointA = updatePoint(map, pointA, null)
    pointB = updatePoint(map, pointB, null)
  } else {
    // Remove all route visuals if not being used
    clearWaypointMarkers()
    pointA = updatePoint(map, pointA, placeA?.geometry || null)
    pointB = updatePoint(map, pointB, placeB?.geometry || null)
  }
}

let googleLoadStateCache: any
let placeACache: any
let placeBCache: any
let journeyResultCache: any
let relatedDataCache: any
let selectedRouteIndexCache: any

export const useJourneyPlannerMap = () => {
  const history = useHistory()
  const search = useLocation().search
  const googleLoadState = useGoogleLoadState()
  const placeAState = usePlaceStateA()
  const placeBState = usePlaceStateB()
  const journeyResultState = useJourneyResultState()
  const selectedRouteIndexState = useSelectedRouteIndexState()
  const [, updateCollection] = useMarkerCollection()
  const filterState = useFilterState()

  useEffect(() => {
    const googleLoaded = googleLoadState.get()
    const placeA = placeAState.get()
    const placeB = placeBState.get()
    const journeyResult = journeyResultState.get()
    const selectedRouteIndex = selectedRouteIndexState.get()
    const mapFilters = filterState.get()
    const map = getMap()
    if (googleLoaded && map) {
      const loadedRelated = !!journeyResult?.routes?.some((r) => r?.properties.hasLoadedRelated)
      if (
        googleLoaded !== googleLoadStateCache ||
        placeA?.search !== placeACache ||
        placeB?.search !== placeBCache ||
        journeyResult?.locationHash !== journeyResultCache ||
        selectedRouteIndex !== selectedRouteIndexCache ||
        loadedRelated !== relatedDataCache
      ) {
        renderDirections(map, journeyResult, placeA, placeB, selectedRouteIndex, history, search)
      }

      // Get all related feature excluding general warnings (these shouldn't show on map)
      let features: RelatedItem[] =
        journeyResult?.routes?.[selectedRouteIndex]?.properties?.related?.filter((feature: RelatedItem) => {
          return feature.properties.type !== 'general-warnings' && mapFilters?.[feature.properties.type].enabled
        }) || []
      // Need to get features from Proxy objects
      features = JSON.parse(JSON.stringify(features))

      // Need to always inlcude closures when routes are plotted but ensure no doubled up on events
      let closures = getClosures()

      features.forEach((f) => {
        closures.forEach((c) => {
          if (f.properties.id === c.properties.id) {
            const index = features.indexOf(f)

            features.splice(index, 1)
          }
        })
      })
      const uniqueEvents = features.concat(closures)

      // Update rendered marker collection with new features
      updateCollection(uniqueEvents)

      relatedDataCache = loadedRelated
      googleLoadStateCache = googleLoaded
      placeACache = placeA?.search
      placeBCache = placeB?.search
      journeyResultCache = journeyResult?.locationHash
      selectedRouteIndexCache = selectedRouteIndex
    }
    // eslint-disable-next-line
  }, [googleLoadState, placeAState, placeBState, journeyResultState, selectedRouteIndexState])

  useEffect(() => {
    return () => {
      clearMarkerCollection()
      clearRoutePolylines()
      clearWaypointMarkers()
      const map = getMap()
      if (map) {
        if (pointA) {
          map.data.remove(pointA)
        }
        if (pointB) {
          map.data.remove(pointB)
        }
      }
    }
  }, [])
}
